import React, { useState, useContext } from 'react';
import { Configuration } from '../../data/configuration.js';
import { CloudData } from '../../data/cloud-data.ts';
import { Utilities } from '../../utilities/utilities.js';
import SummaryStepOne from './summary-step-one.tsx'
import SummaryStepTwo from './summary-step-two.tsx'
import { CameraController } from '../../camera-controller.ts';
import DisabledOverlay from '../ui/disabled-overlay.tsx';
import { AppContext, ViewStates } from '../app.tsx';
import { SummaryImage } from '../ui/summary-image.tsx';
import { SendToDealerView } from '../summary/save-configuration/send-to-dealer-view.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export enum Views {
	SummaryStepOne,
	SummaryStepTwo,
	SendToDealer
}

interface SummaryViewProps {
	onBackButtonClicked: () => void;
	cameraController: CameraController;
	startOnStepTwo: boolean;
	isMobile: boolean;
}

interface SummaryHeaderProps {
	showPrice: boolean;
	totalCost?: (price: number, invalidText?: string) => string;
	cameraController: CameraController;
	setIsDisabled: (isDisabled: boolean) => void;
}

interface SummaryFooterProps {
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
	nextButtonText?: string;
	nextButtonDisabled?: boolean;
	hideNextButton?: boolean;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function SummaryView(props: SummaryViewProps) {

	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// states.

	// the send to dealer flow returns the customer to the options they launched it from, not to the top of
	// the summary. on mobile that flow replaces this whole view, so app.tsx has to say where to re-open.
	const [view, setView] = useState(props.startOnStepTwo ? Views.SummaryStepTwo : Views.SummaryStepOne);
	const [isDisabled, setIsDisabled] = useState(false);

	// handlers.

	// returns the total cost or a suitable message (if an invalid option has been selected).
	const totalCost = (price: number, invalidText: string = '') => {
		if (!Configuration.invalidOptionSelected) {
			return Utilities.asCurrency(price, Configuration.regionCode);
		}
		return invalidText;
	};

	// n.b. restart now lives on the vehicle panel - see AppContext.restartConfiguration in app.tsx.
	const startSendToDealer = () => {
		if (props.isMobile) {
			context.handleChangeView(ViewStates.SendToDealer);
		} else {
			setView(Views.SendToDealer);
		}
	}

	const handleBackButtonClicked = () => {
		if (view === Views.SummaryStepTwo) {
			setView(Views.SummaryStepOne);
		} else if (view === Views.SendToDealer) {
			setView(Views.SummaryStepTwo);
		} else {
			props.onBackButtonClicked();
		}
	}

	// conditionally render views.
	const currentView = () => {
		switch (view) {
			case Views.SummaryStepOne:

				// present the step one view.
				return (
					<SummaryStepOne totalCost={totalCost}  />
				);

			case Views.SummaryStepTwo:

				// present the step two view.
				return (
					<SummaryStepTwo
						cameraController={props.cameraController}
						setIsDisabled={setIsDisabled}
						onSendToDealerClicked={startSendToDealer}
					/>
				);

			case Views.SendToDealer: 

				// present the send to dealer view. on completion we return to step two, the options it was launched
				// from - the mobile flow lands there too, via the startOnStepTwo prop.
				return (
					<SendToDealerView
						cameraController={props.cameraController}
						onBackButtonClicked={handleBackButtonClicked}
						onComplete={() => setView(Views.SummaryStepTwo)}
					/>
				);

			default:
				return null;
		}
	};

	// move to the next page. only step one offers a next button, so there is nothing else to handle.
	const handleNextButtonClicked = () => {
		setView(Views.SummaryStepTwo);
	}

	// step two is the end of the flow, so it shows back on its own.
	const hideNextButton = (view === Views.SummaryStepTwo);

	// return the HTML.
	return (
		<div className="summary-container">
			<div className="summary-main-section">
				<SummaryHeader
					showPrice={true}
					totalCost={totalCost}
					cameraController={props.cameraController}
					setIsDisabled={setIsDisabled}
				/>
				<div className="summary-content">
					{currentView()}
				</div>
			</div>
			{view !== Views.SendToDealer && (
				<div className="summary-footer-container">
					<SummaryFooter
						onNextButtonClicked={handleNextButtonClicked}
						onBackButtonClicked={handleBackButtonClicked}
						hideNextButton={hideNextButton}
					/>
				</div>
			)}
			{isDisabled && <DisabledOverlay />}
		</div>
	);
}

export function SummaryHeader(props: SummaryHeaderProps) {
	const modelCost = CloudData.getPrice(Configuration.modelId, CloudData.modelOptions, { ref: false });

	const placeholderText = 'Contact Dealer';
	let grandTotal:string | null = null;
	if (props.showPrice && props.totalCost) {
		grandTotal = props?.totalCost(Configuration.totalCost, placeholderText);
	}

	return (
		<div className="summary-header">
			<div className="title-container">
				<img {...Configuration.logoProps()} />
				{props.showPrice && props.totalCost && (
					<React.Fragment>
						{grandTotal !== placeholderText && (
							<div className="specification-summary-price">
								<p id="specification-summary">Specification Summary</p>
								<div>
									<div className="price-container">
										<p>Base Price:</p>
										<p>{Utilities.asCurrency(modelCost, Configuration.regionCode)}</p>
									</div>
									<div className="price-container">
										<p>Selected Options:</p>
										<p>{Utilities.asCurrency(Configuration.totalCost - modelCost, Configuration.regionCode)}</p>
									</div>
								</div>
							</div>
						)}
						<div className="grand-total">
							<div className="price-container">
								<p>Grand Total:</p>
								<p>{grandTotal}</p>
							</div>
							{Configuration.isUsRegion() ?
								<p id="msrp-disclaimer">MSRP shown excludes tax, title, license, dealer and destination fees.<br />Price does not include current U.S. import tariffs. Final price may vary.</p> :
								<p id="msrp-disclaimer">The prices shown are correct at the time of writing and are subject to change without notice. On the road and registration costs are subject to additional charges and may differ by region. All pricing should be confirmed with the supplying dealer.</p>
							}
						</div>
					</React.Fragment>
				)}
			</div>
			<SummaryImage cameraController={props.cameraController} setIsDisabled={props.setIsDisabled} className="summary-header-img" />
		</div>
	);
}

export function SummaryFooter(props: SummaryFooterProps) {
	return (
		<div className="summary-footer">
			<div className="summary-button-container">
				<button className="summary-button" onClick={props.onBackButtonClicked}>
					<img src="assets/images/icons/up-arrow.svg" width="18" height="10" alt="<" />
					<p>BACK</p>
				</button>

				{/* when there is no next step the button is hidden rather than removed, so back keeps exactly the size and position it has elsewhere. */}
				<button
					className={`summary-button${props.hideNextButton ? ' is-hidden' : ''}`}
					onClick={props.onNextButtonClicked}
					disabled={props.nextButtonDisabled || props.hideNextButton}
					aria-hidden={props.hideNextButton}
					tabIndex={props.hideNextButton ? -1 : undefined}
				>
					<p>{props.nextButtonText || 'NEXT'}</p>
					<img src="assets/images/icons/down-arrow.svg" width="18" height="10" alt=">" />
				</button>
			</div>
		</div>
	);
}
